"use client";

// ============================================================
// CLUBHUB – Aushang schreiben oder bearbeiten
// ============================================================
// Einfacher Text — eine Leerzeile beginnt einen neuen Absatz, mehr
// Formatierung braucht ein Aushang nicht. Zielgruppe, Anheften und
// ein Ablaufdatum, nach dem er von selbst verschwindet. Darunter
// steht, wer ihn sieht: nur angemeldete Mitglieder, nie die
// Oeffentlichkeit.
// ============================================================
import { useState } from "react";
import { Lock, Trash2 } from "lucide-react";
import {
  Checkbox,
  Dialog,
  DialogCancel,
  DialogChoice,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogFooterStart,
  DialogForm,
  DialogHeader,
  DialogNotice,
  DialogRow,
  DialogSideAction,
  DialogSubmit,
  DialogTitle,
  Input,
  Select,
  Textarea,
  confirmDialog,
  toast,
} from "@/components/ui";
import type { PostAudience } from "@/generated/prisma/enums";
import { istFehler, type ActionResult } from "@/lib/action-result";
import type { AushangView } from "@/lib/pinnwand/views";
import { ablaufAus, absaetze } from "@/lib/pinnwand/logik";
import { istIsoDatum } from "@/lib/gruppen/datum";
import { formatDate } from "@/lib/utils";
import { usePinnwand } from "./pinnwand-bestand";

export function AushangDialog({
  offen,
  onClose,
  aushang,
}: {
  offen: boolean;
  onClose: () => void;
  aushang?: AushangView;
}) {
  return (
    <Dialog open={offen} onClose={onClose} size="wide">
      {offen && <Formular aushang={aushang} onClose={onClose} />}
    </Dialog>
  );
}

type Fehler = Partial<Record<"title" | "body" | "groupId" | "expiresAt", string>>;

function Formular({ aushang, onClose }: { aushang?: AushangView; onClose: () => void }) {
  const { gruppen, heute, quelle, aushangSpeichern, aushangLoeschen } = usePinnwand();
  const [title, setTitle] = useState(aushang?.title ?? "");
  const [body, setBody] = useState(aushang?.body ?? "");
  const [audience, setAudience] = useState<PostAudience>(aushang?.audience ?? "ALL");
  const [groupId, setGroupId] = useState(aushang?.groupId ?? "");
  const [isPinned, setPinned] = useState(aushang?.isPinned ?? false);
  const [ablauf, setAblauf] = useState(aushang?.expiresAt?.slice(0, 10) ?? "");
  const [fehler, setFehler] = useState<Fehler>({});

  const gruppe = gruppen.find((g) => g.id === groupId);
  const anzahlAbsaetze = absaetze(body).length;

  async function speichern(): Promise<ActionResult<{ titel: string }>> {
    const t = title.trim();
    if (t.length < 3) return { error: "Bitte eine Überschrift mit mindestens drei Zeichen.", field: "title" };
    if (body.trim().length < 10) return { error: "Bitte ein paar Worte mehr — mindestens zehn Zeichen.", field: "body" };
    if (audience === "GROUP" && !groupId) return { error: "Bitte die Gruppe wählen, die den Aushang sieht.", field: "groupId" };
    if (ablauf && !istIsoDatum(ablauf)) return { error: "Bitte ein gültiges Datum.", field: "expiresAt" };
    if (ablauf && ablauf < heute) return { error: "Das Ablaufdatum liegt in der Vergangenheit.", field: "expiresAt" };
    const r = await aushangSpeichern(
      {
        title: t,
        body: body.trim(),
        audience,
        groupId: audience === "GROUP" ? groupId : null,
        isPinned,
        expiresAt: ablaufAus(ablauf || null),
      },
      aushang?.id,
    );
    return istFehler(r) ? r : { success: true, titel: r.aushang.title };
  }

  async function loeschen() {
    if (!aushang) return;
    const ja = await confirmDialog({
      title: `„${aushang.title}“ löschen?`,
      description: "Der Aushang verschwindet für alle. Soll er nur nicht mehr oben stehen, lass ihn ablaufen.",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ja) return;
    const r = await aushangLoeschen(aushang.id);
    if (istFehler(r)) {
      toast.error(r.error);
      return;
    }
    toast.success("Aushang gelöscht");
    onClose();
  }

  return (
    <DialogForm
      action={speichern}
      refresh={false}
      success={(r) =>
        "titel" in r
          ? aushang
            ? `„${r.titel}“ gespeichert`
            : quelle === "muster"
              ? { title: `„${r.titel}“ hängt an der Pinnwand`, description: "Im Musterbestand nur bis zum Neuladen — gespeichert wird nach der Anmeldung." }
              : `„${r.titel}“ hängt an der Pinnwand`
          : null
      }
      onError={(m, r) => {
        const feld = r && "field" in r ? (r.field as keyof Fehler) : undefined;
        if (feld) setFehler({ [feld]: m });
        else toast.error(m);
      }}
    >
      <DialogHeader>
        <DialogTitle>{aushang ? "Aushang bearbeiten" : "Neuer Aushang"}</DialogTitle>
        <DialogDescription>
          {aushang ? `Von ${aushang.authorName}, ${formatDate(aushang.createdAt)}` : "Was alle wissen sollen — kurz, mit Datum und Ort, wenn es um einen Termin geht."}
        </DialogDescription>
      </DialogHeader>

      <DialogContent>
        <div className="grid grid-cols-1 gap-x-10 gap-y-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <div className="min-w-0 space-y-4">
            <Input
              name="title"
              label="Überschrift"
              required
              maxLength={120}
              autoComplete="off"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setFehler((f) => ({ ...f, title: undefined }));
              }}
              placeholder="z. B. Training fällt am Dienstag aus"
              error={fehler.title}
            />
            <Textarea
              name="body"
              label="Text"
              rows={9}
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setFehler((f) => ({ ...f, body: undefined }));
              }}
              placeholder={"Was, wann, wo, für wen.\n\nEine Leerzeile beginnt einen neuen Absatz."}
              error={fehler.body}
              hint={anzahlAbsaetze > 1 ? `${anzahlAbsaetze} Absätze` : "Einfacher Text. Eine Leerzeile beginnt einen neuen Absatz."}
            />
          </div>

          <div className="min-w-0 space-y-4">
            <DialogChoice<PostAudience>
              label="Wer sieht den Aushang?"
              value={audience}
              onChange={(v) => {
                setAudience(v);
                setFehler((f) => ({ ...f, groupId: undefined }));
              }}
              options={[
                { value: "ALL", label: "Alle Mitglieder", hint: "Im Mitgliederbereich und in der App" },
                { value: "BOARD", label: "Vorstand", hint: "Nur Vorstand und Verwaltung" },
                { value: "GROUP", label: "Eine Gruppe", hint: "Nur die aktuellen Mitglieder der Gruppe" },
              ]}
            />
            {audience === "GROUP" && (
              <Select
                name="groupId"
                label="Gruppe"
                value={groupId}
                onChange={(v) => {
                  setGroupId(v);
                  setFehler((f) => ({ ...f, groupId: undefined }));
                }}
                placeholder="Gruppe wählen …"
                options={gruppen.map((g) => ({ value: g.id, label: g.name }))}
                error={fehler.groupId}
                hint={gruppe ? `Sorgeberechtigte minderjähriger Mitglieder der ${gruppe.name} sehen ihn mit.` : undefined}
              />
            )}
            <DialogRow>
              <Input
                type="date"
                name="expiresAt"
                label="Läuft ab am"
                value={ablauf}
                min={heute}
                onChange={(e) => {
                  setAblauf(e.target.value);
                  setFehler((f) => ({ ...f, expiresAt: undefined }));
                }}
                error={fehler.expiresAt}
                hint={ablauf ? "Danach steht er unter „Abgelaufen“." : "Leer: bleibt, bis jemand ihn abnimmt."}
              />
              <div className="flex items-end pb-2.5">
                <Checkbox
                  name="isPinned"
                  checked={isPinned}
                  onChange={(e) => setPinned(e.target.checked)}
                  label="Oben anheften"
                  hint="Steht vor allen anderen"
                />
              </div>
            </DialogRow>
          </div>
        </div>

        <DialogNotice icon={<Lock className="text-text-tertiary h-4 w-4" />}>
          Aushänge sehen nur angemeldete Mitglieder — nie die Öffentlichkeit oder die Website. Keine Zugangsdaten, Gesundheits- oder Kontodaten aushängen; Passwörter gehören nicht an die Pinnwand, auch nicht an die des Vorstands.
        </DialogNotice>
      </DialogContent>

      <DialogFooter>
        {aushang && (
          <DialogFooterStart>
            <DialogSideAction tone="danger" onClick={() => void loeschen()}>
              <Trash2 className="h-4 w-4" aria-hidden />
              Löschen
            </DialogSideAction>
          </DialogFooterStart>
        )}
        <DialogCancel />
        <DialogSubmit>{aushang ? "Speichern" : "Aushängen"}</DialogSubmit>
      </DialogFooter>
    </DialogForm>
  );
}
